import { type ReactNode, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import {
  ContextMenuItem,
  ContextMenuSeparator,
} from '@/components/ui/context-menu';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { inElectron } from '@/utils/capabilities';

export function SidebarItemMenuItems({
  variant,
  title,
  isFavorited,
  onOpenInTab,
  onRename,
  onFavorite,
  onMarkUnread,
  onArchive,
  onDelete,
  extra,
}: {
  variant: 'context' | 'dropdown';
  title: string;
  isFavorited?: boolean | null;
  onOpenInTab?: () => void;
  onRename?: () => void;
  onFavorite?: () => void;
  onMarkUnread?: () => void;
  onArchive?: () => void;
  onDelete?: () => void;
  extra?: ReactNode;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const openInTabHandler = inElectron() ? onOpenInTab : undefined;

  if (variant === 'dropdown') {
    return (
      <>
        {openInTabHandler && (
          <DropdownMenu.Item
            onClick={openInTabHandler}
            aria-label={`Open ${title} in tab`}
          >
            <Icons.arrowUpRight />
            Open in tab
          </DropdownMenu.Item>
        )}
        {extra}
        {onRename && (
          <DropdownMenu.Item onClick={onRename} aria-label={`Rename ${title}`}>
            <Icons.edit />
            Rename
          </DropdownMenu.Item>
        )}
        {onFavorite && (
          <DropdownMenu.Item
            onClick={onFavorite}
            aria-label={
              isFavorited
                ? `Remove ${title} from favorites`
                : `Add ${title} to favorites`
            }
          >
            {isFavorited ? <Icons.starFilled /> : <Icons.star />}
            {isFavorited ? 'Unfavorite' : 'Favorite'}
          </DropdownMenu.Item>
        )}
        {onMarkUnread && (
          <DropdownMenu.Item
            onClick={onMarkUnread}
            aria-label={`Mark ${title} as unread`}
          >
            <Icons.mail />
            Mark as unread
          </DropdownMenu.Item>
        )}
        {onArchive && (
          <DropdownMenu.Item
            onClick={onArchive}
            aria-label={`Archive ${title}`}
          >
            <Icons.archive />
            Archive
          </DropdownMenu.Item>
        )}
        {onDelete && (
          <>
            <DropdownMenu.Separator />
            <DropdownMenu.ItemWithConfirm
              onConfirm={onDelete}
              icon={Icons.trash}
              ariaLabel={`Delete ${title}`}
            >
              Delete
            </DropdownMenu.ItemWithConfirm>
          </>
        )}
      </>
    );
  }

  return (
    <>
      {openInTabHandler && (
        <ContextMenuItem
          onClick={openInTabHandler}
          role="menuitem"
          aria-label={`Open ${title} in tab`}
          icon={Icons.arrowUpRight}
        >
          Open in tab
        </ContextMenuItem>
      )}
      {extra}
      {onRename && (
        <ContextMenuItem
          onClick={onRename}
          role="menuitem"
          aria-label={`Rename ${title}`}
          icon={Icons.edit}
        >
          Rename
        </ContextMenuItem>
      )}
      {onFavorite && (
        <ContextMenuItem
          onClick={onFavorite}
          role="menuitem"
          aria-label={
            isFavorited
              ? `Remove ${title} from favorites`
              : `Add ${title} to favorites`
          }
          icon={isFavorited ? Icons.starFilled : Icons.star}
        >
          {isFavorited ? 'Unfavorite' : 'Favorite'}
        </ContextMenuItem>
      )}
      {onMarkUnread && (
        <ContextMenuItem
          onClick={onMarkUnread}
          role="menuitem"
          aria-label={`Mark ${title} as unread`}
          icon={Icons.mail}
        >
          Mark as unread
        </ContextMenuItem>
      )}
      {onArchive && (
        <ContextMenuItem
          onClick={onArchive}
          role="menuitem"
          aria-label={`Archive ${title}`}
          icon={Icons.archive}
        >
          Archive
        </ContextMenuItem>
      )}
      {onDelete && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem
            onClick={(e) => {
              setConfirmDelete((prev) => {
                const next = !prev;

                if (next) {
                  e.preventDefault();
                  e.stopPropagation();
                } else {
                  onDelete();
                }

                return next;
              });
            }}
            className="text-danger-9"
            role="menuitem"
            aria-label={`Delete ${title}`}
            icon={confirmDelete ? Icons.check : Icons.trash}
            iconClassName={
              confirmDelete
                ? 'motion-scale-in-50 motion-duration-200'
                : undefined
            }
          >
            {confirmDelete ? (
              <span className="motion-opacity-in-0 motion-duration-100">
                Confirm?
              </span>
            ) : (
              'Delete'
            )}
          </ContextMenuItem>
        </>
      )}
    </>
  );
}
